import { useCallback, useEffect, useRef, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { COCKPIT_ROLES, decodeRoles, initializeSession } from '../../lib/auth'
import { type Lang } from '../../lib/i18n'
import {
  advanceOrderStatus, cancelOrder, fetchOrder, finalCheckOrder, formatOrderId, isAlreadyThere, recordDeliveryCash,
  twoDecimals, type OrderDetail, type OrderStatusValue, type SendOutcome,
} from '../../lib/orders-api'
import { cancelHeld, enqueueHeld, kickDrain, releaseHeld, takeHeld } from '../../lib/idb-queue'
import { fillSlots } from '../board/board-i18n'
import { Button } from '../../components/ui/button'
import { CommandHeader } from '../../components/CommandHeader'
import { Input } from '../../components/ui/input'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog'
import { ActivityTimeline } from './sections/ActivityTimeline'
import { CustomerSection } from './sections/CustomerSection'
import { DeliverySection } from './sections/DeliverySection'
import { HeaderSection } from './sections/HeaderSection'
import { ItemsSection } from './sections/ItemsSection'
import { TotalsSection } from './sections/TotalsSection'
import { UndoToast } from './UndoToast'
import { ot } from './orders-i18n'

const UNDO_WINDOW_S = 7

// R517 (Frank #66769, Rook #66772): the same held-at-tap write as BoardScreen. The move is in the
// queue from the tap, held until the window ends. One sender: this screen at the window end, after
// takeHeld consumes the entry (false = a drain already has it). Leaving the screen or moving to
// another order inside the window releases it (sendAfter cleared) and kicks one drain.
interface HeldMove {
  orderId: string
  toStatus: OrderStatusValue
  expectedStatus: OrderStatusValue
  // The queue entry's uuid; null when it could not be written (the screen then sends as before).
  held: Promise<string | null>
}

function holdMove(orderId: string, toStatus: OrderStatusValue, expectedStatus: OrderStatusValue): HeldMove {
  const held = Promise.resolve()
    .then(() => enqueueHeld('PATCH', `/api/admin/orders/${orderId}/status`, { toStatus, expectedStatus }, UNDO_WINDOW_S * 1000))
    .catch(() => null)
  return { orderId, toStatus, expectedStatus, held }
}

/** Undo (or a new tap replacing the move): the held entry is deleted, nothing is sent. */
function cancelMove(move: HeldMove): void {
  void move.held.then((uuid) => (uuid === null ? false : cancelHeld(uuid))).catch(() => undefined)
}

/** The window end: true when this screen may send (it consumed the held entry). */
async function takeMove(move: HeldMove): Promise<boolean> {
  const uuid = await move.held
  if (uuid === null) return true
  return takeHeld(uuid).catch(() => true)
}

/** Leaving inside the window: never dropped. Released to the queue, then one drain now. */
async function releaseMove(move: HeldMove): Promise<void> {
  const uuid = await move.held
  if (uuid === null) {
    await advanceOrderStatus(move.orderId, move.toStatus, { expectedStatus: move.expectedStatus }).catch(() => undefined)
    return
  }
  if (await releaseHeld(uuid).catch(() => false)) await Promise.resolve().then(() => kickDrain()).catch(() => undefined)
}

interface Props {
  lang: Lang
  onLangToggle: () => void
  isOnline: boolean
}

interface PendingAdvance {
  toStatus: OrderStatusValue
  secondsLeft: number
}

// R264 (b): the line under the header that names the order and what became of the last write.
type Notice =
  | { kind: 'fail'; message: string }
  | { kind: 'kitchen'; count: string; toStatus: OrderStatusValue; expectedStatus: OrderStatusValue }

export function OrderDetailScreen({ lang, onLangToggle, isOnline }: Props) {
  const { id } = useParams<{ id: string }>()
  const [authState, setAuthState] = useState<'checking' | 'ok' | 'redirect'>('checking')
  const [roles, setRoles] = useState<string[]>([])
  const [order, setOrder] = useState<OrderDetail | null>(null)
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [pending, setPending] = useState<PendingAdvance | null>(null)
  const timersRef = useRef<{ commit: number; tick: number; move: HeldMove } | null>(null)
  // bench-HOP-MARK-DELIVERED-CASH.CB-A: Mark delivered's cash prompt. Independent of the
  // generic forward-advance flow above (getForwardAdvanceTargets deliberately yields ZERO
  // targets for OUT_FOR_DELIVERY — bench-HOP-ORDER-BOARD.CB-A's money-path-leak fix — so
  // this control is NOT routed through HeaderSection/handleAdvance; it is its own trigger,
  // scoped to this screen only).
  const [cashDialogOpen, setCashDialogOpen] = useState(false)
  const [cashAmount, setCashAmount] = useState('')
  const [cashBusy, setCashBusy] = useState(false)
  const [cashError, setCashError] = useState<string | null>(null)
  // bench-HOP-SAYS-WHY.CB-A §1③: the server's reason for a cash-write refusal, shown as a
  // second line under the designed markDeliveredCashFailed copy. Separate from cashError
  // (which also carries the client-side amount-validation message, a single line on its own).
  const [cashErrorDetail, setCashErrorDetail] = useState<string | null>(null)
  // bench-HOP-CAN-FINAL-CHECK.CB-F: Bilal's final check — built like handleAdvance
  // (pending state disables the button, the write goes through the same offline queue),
  // but refetches afterward instead of an optimistic undo-window flip: the status this
  // writes (READY_FOR_DISPATCH) is not itself a forward-advance target on this screen.
  const [finalCheckBusy, setFinalCheckBusy] = useState(false)
  // R264 (b): every order write waits for the server's reply; the header stays disabled meanwhile.
  const [sending, setSending] = useState(false)
  const sendingRef = useRef(false)
  const [notice, setNotice] = useState<Notice | null>(null)
  // R460: Back returns to the HOP screen that opened this order, else to the Board.

  useEffect(() => {
    initializeSession().then(() => {
      const r = decodeRoles()
      setRoles(r)
      setAuthState(r.some((role) => (COCKPIT_ROLES as readonly string[]).includes(role)) ? 'ok' : 'redirect')
    })
  }, [])

  const load = useCallback(() => {
    if (!id) return
    setLoadState('loading')
    fetchOrder(id)
      .then((o) => {
        setOrder(o)
        setLoadState('ready')
      })
      .catch(() => setLoadState('error'))
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  const clearTimers = useCallback((): HeldMove | null => {
    const tm = timersRef.current
    if (!tm) return null
    window.clearTimeout(tm.commit)
    window.clearInterval(tm.tick)
    timersRef.current = null
    return tm.move
  }, [])

  // Clear any in-flight undo timer on unmount or when navigating to a different order. R517: the
  // move inside its window is released to the queue and sent by one drain now, never dropped.
  useEffect(() => () => {
    const move = clearTimers()
    if (move) {
      setPending(null)
      void releaseMove(move)
    }
  }, [id, clearTimers])

  // One write, awaited: `sending` until the reply, then the line for a refusal, then the reload
  // (after the reply, so it cannot race ahead of the write and read stale data).
  const runWrite = useCallback(
    async (send: () => Promise<SendOutcome>, onReply?: (res: SendOutcome) => Notice | null) => {
      if (!order || sendingRef.current) return
      sendingRef.current = true
      setSending(true)
      setNotice(null)
      let next: Notice | null = null
      try {
        const res = await send()
        if (onReply) next = onReply(res)
        else if (res.outcome === 'rejected') next = { kind: 'fail', message: res.message }
      } catch (err) {
        next = { kind: 'fail', message: err instanceof Error ? err.message : String(err) }
      }
      sendingRef.current = false
      setSending(false)
      setNotice(next)
      load()
    },
    [order, load],
  )

  const sendAdvance = useCallback(
    (toStatus: OrderStatusValue, expectedStatus: OrderStatusValue, overrideKitchen?: true) => {
      if (!order) return Promise.resolve()
      const orderId = order.id
      return runWrite(
        () => advanceOrderStatus(orderId, toStatus, overrideKitchen ? { expectedStatus, overrideKitchen } : { expectedStatus }),
        (res): Notice | null => {
          if (res.outcome === 'sent' || res.outcome === 'queued' || isAlreadyThere(res, toStatus)) return null
          if (res.code === 'KITCHEN_OPEN') {
            return { kind: 'kitchen', count: String(res.details?.openMealItems ?? ''), toStatus, expectedStatus }
          }
          return { kind: 'fail', message: res.message }
        },
      )
    },
    [order, runWrite],
  )

  const handleAdvance = useCallback(
    (toStatus: OrderStatusValue) => {
      if (!order || sendingRef.current) return
      const expectedStatus = order.status
      const replaced = clearTimers()
      if (replaced) cancelMove(replaced)
      const move = holdMove(order.id, toStatus, expectedStatus)
      setPending({ toStatus, secondsLeft: UNDO_WINDOW_S })

      const tick = window.setInterval(() => {
        setPending((p) => (p ? { ...p, secondsLeft: p.secondsLeft - 1 } : p))
      }, 1000)

      const commit = window.setTimeout(() => {
        window.clearInterval(tick)
        timersRef.current = null
        setPending(null)
        // No optimistic flip: the order keeps its status, the header disabled, until the reply.
        void takeMove(move).then((mine) => {
          if (mine) return sendAdvance(toStatus, expectedStatus)
        })
      }, UNDO_WINDOW_S * 1000)

      timersRef.current = { commit, tick, move }
    },
    [order, sendAdvance, clearTimers],
  )

  const handleUndo = useCallback(() => {
    const move = clearTimers()
    if (move) cancelMove(move)
    setPending(null)
  }, [clearTimers])

  const handleFinalCheck = useCallback(async () => {
    if (!order) return
    setFinalCheckBusy(true)
    try {
      let expected: string | undefined
      try {
        expected = twoDecimals(order.totalAmount)
      } catch {
        expected = undefined // unreadable total: send unguarded, as before
      }
      await runWrite(() => finalCheckOrder(order.id, expected))
    } finally {
      setFinalCheckBusy(false)
    }
  }, [order, runWrite])

  const handleCancel = useCallback(async () => {
    if (!order) return
    await runWrite(() => cancelOrder(order.id))
  }, [order, runWrite])

  // bench-HOP-MARK-DELIVERED-CASH.CB-A: record first, advance second — the reverse (today's
  // behaviour) fails the server's cash gate on every COD delivery. A failed cash write must
  // NOT advance the status; a 409 (already recorded) is not a failure and proceeds same as
  // a fresh write.
  const handleMarkDelivered = useCallback(async () => {
    if (!order) return
    const amountUsd = Number(cashAmount)
    if (!Number.isFinite(amountUsd) || amountUsd < 0) {
      setCashError(ot(lang, 'markDeliveredAmountInvalid'))
      setCashErrorDetail(null)
      return
    }
    setCashBusy(true)
    setCashError(null)
    setCashErrorDetail(null)
    try {
      await recordDeliveryCash(order.id, amountUsd)
    } catch (err) {
      setCashBusy(false)
      setCashError(ot(lang, 'markDeliveredCashFailed'))
      setCashErrorDetail(err instanceof Error ? err.message : null)
      return
    }
    let outcome: SendOutcome | null = null
    let thrown: string | null = null
    try {
      outcome = await advanceOrderStatus(order.id, 'DELIVERED', { expectedStatus: 'OUT_FOR_DELIVERY' })
    } catch (err) {
      thrown = err instanceof Error ? err.message : String(err)
    }
    setCashBusy(false)
    if (thrown !== null || (outcome?.outcome === 'rejected' && !isAlreadyThere(outcome, 'DELIVERED'))) {
      // Refused: the dialog stays open with the order and the reason in its error slot.
      const message = thrown ?? (outcome?.outcome === 'rejected' ? outcome.message : '')
      setCashError(fillSlots(ot(lang, 'actionFailed'), { code: formatOrderId(order.id, order.shortCode), message }))
      setCashErrorDetail(null)
    } else {
      setCashDialogOpen(false)
      setCashAmount('')
    }
    load()
  }, [order, cashAmount, lang, load])

  // hop:reconcile (window CustomEvent, fired by idb-queue's drainQueue on a 400 conflict —
  // same channel ActivityFeed listens on) — the optimistic update above raced a concurrent
  // change; re-fetch to show the real state.
  useEffect(() => {
    const handler = () => load()
    window.addEventListener('hop:reconcile', handler)
    return () => window.removeEventListener('hop:reconcile', handler)
  }, [load])

  // bench-HOP-SAYS-WHY.CB-A §1④: hop:action-failed (idb-queue's drainQueue, a terminal 4xx
  // OR a still-retrying 5xx) — a refused queued write (e.g. the Mark-delivered status PATCH)
  // must not leave the optimistic DELIVERED on screen. Re-fetch exactly as hop:reconcile does.
  // Matched with the trailing slash so order "12"'s listener does not fire for order "123"'s
  // event (both start with "/api/admin/orders/12").
  useEffect(() => {
    if (!id) return
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { url?: string } | undefined
      if (detail?.url?.includes(`/api/admin/orders/${id}/`) || detail?.url?.includes(`/api/orders/${id}/final-check`)) load()
    }
    window.addEventListener('hop:action-failed', handler)
    return () => window.removeEventListener('hop:action-failed', handler)
  }, [id, load])

  if (authState === 'redirect') return <Navigate to="/login" replace />

  const dir = lang === 'ar' ? 'rtl' : 'ltr'
  const pageClass = lang === 'ar' ? 'min-h-screen bg-background font-arabic' : 'min-h-screen bg-background font-sans'
  // R492: every face of this screen draws the shell, so a failed load still has its Back.
  const shell = (
    <div className="sticky top-0 z-10 shadow-sm">
      <CommandHeader
        title={order ? formatOrderId(order.id, order.shortCode) : formatOrderId(id ?? '')}
        lang={lang}
        onLangToggle={onLangToggle}
        isOnline={isOnline}
      />
    </div>
  )

  if (authState === 'checking' || loadState === 'loading') {
    return (
      <div dir={dir} className={pageClass}>
        {shell}
        <div className="flex min-h-[60vh] items-center justify-center text-muted-foreground">{ot(lang, 'loading')}</div>
      </div>
    )
  }
  if (loadState === 'error' || !order) {
    return (
      <div dir={dir} className={pageClass}>
        {shell}
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 text-center">
          <p className="text-muted-foreground">{ot(lang, 'errorLoading')}</p>
          <Button size="lg" className="min-h-[48px]" onClick={load}>
            {ot(lang, 'retry')}
          </Button>
        </div>
      </div>
    )
  }

  const displayStatus = pending ? pending.toStatus : order.status

  return (
    <div dir={dir} className={pageClass}>
      {shell}

      <main className="mx-auto max-w-3xl space-y-4 px-4 py-6 pb-24">
        <HeaderSection
          order={order}
          lang={lang}
          roles={roles}
          displayStatus={displayStatus}
          pending={!!pending || finalCheckBusy || sending}
          onAdvance={handleAdvance}
          onCancel={handleCancel}
          onFinalCheck={handleFinalCheck}
          isOnline={isOnline}
          onItemsChanged={load}
        />
        {notice && (
          <div role="alert" className="flex min-h-[44px] flex-wrap items-center justify-between gap-3 rounded-xl border border-destructive/30 bg-card px-4 py-3 text-sm text-destructive">
            <p className="min-w-0 flex-1 break-words">
              {notice.kind === 'kitchen'
                ? fillSlots(ot(lang, 'kitchenOpen'), { code: formatOrderId(order.id, order.shortCode), count: notice.count })
                : fillSlots(ot(lang, 'actionFailed'), { code: formatOrderId(order.id, order.shortCode), message: notice.message })}
            </p>
            {notice.kind === 'kitchen' && (
              <Button
                variant="secondary"
                size="sm"
                className="min-h-[44px]"
                disabled={sending}
                onClick={() => void sendAdvance(notice.toStatus, notice.expectedStatus, true)}
              >
                {ot(lang, 'moveAnyway')}
              </Button>
            )}
          </div>
        )}
        {order.status === 'OUT_FOR_DELIVERY' && order.fulfillmentMethod !== 'pickup' && (
          <div className="rounded-xl border bg-card p-5 shadow-sm">
            <Dialog
              open={cashDialogOpen}
              onOpenChange={(open) => {
                setCashDialogOpen(open)
                if (!open) {
                  setCashAmount('')
                  setCashError(null)
                  setCashErrorDetail(null)
                }
              }}
            >
              <DialogTrigger asChild>
                <Button size="lg" className="h-14 w-full px-6 text-base">
                  {ot(lang, 'markDelivered')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{ot(lang, 'markDeliveredTitle')}</DialogTitle>
                  <DialogDescription>{ot(lang, 'markDeliveredBody')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                  <label htmlFor="mark-delivered-cash-amount" className="text-sm font-medium text-foreground">
                    {ot(lang, 'markDeliveredAmountLabel')}
                  </label>
                  <Input
                    id="mark-delivered-cash-amount"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={cashAmount}
                    disabled={cashBusy}
                    onChange={(e) => setCashAmount(e.target.value)}
                  />
                  {cashError && (
                    <>
                      <p role="alert" className="text-sm text-destructive">{cashError}</p>
                      {cashErrorDetail && <p className="text-sm text-destructive">{cashErrorDetail}</p>}
                    </>
                  )}
                </div>
                <DialogFooter className="gap-2">
                  <Button
                    variant="outline"
                    size="lg"
                    className="min-h-[48px]"
                    disabled={cashBusy}
                    onClick={() => setCashDialogOpen(false)}
                  >
                    {ot(lang, 'markDeliveredDismiss')}
                  </Button>
                  <Button size="lg" className="min-h-[48px]" disabled={cashBusy} onClick={handleMarkDelivered}>
                    {ot(lang, 'markDeliveredConfirm')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
        <CustomerSection customer={order.customer} orderId={order.id} shortCode={order.shortCode} lang={lang} />
        <DeliverySection order={order} lang={lang} roles={roles} isOnline={isOnline} onFeeChanged={load} />
        <ItemsSection items={order.items} lang={lang} currency={order.currency} />
        <TotalsSection order={order} lang={lang} />
        <ActivityTimeline history={order.statusHistory} lang={lang} />
      </main>

      {pending && <UndoToast lang={lang} secondsLeft={pending.secondsLeft} onUndo={handleUndo} />}
    </div>
  )
}
